import React, { Fragment } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';

import { LazyRendererContentStateComponent } from '../lazyLoadables.ts';

import { StyledBorder } from './StyledBorder.tsx';
import type {
	RendererContentStateQuery$data,
	RendererContentStateQuery$variables,
} from './__generated__/RendererContentStateQuery.graphql.ts';
import type {
	RendererContentState_content$data,
	RendererContentState_content$key,
} from './__generated__/RendererContentState_content.graphql.ts';

type RendererContentStateProps = {
	contentId: string;
	isDraft: boolean;
	hasSeparator?: boolean;
	contentFragmentRef?: RendererContentState_content$key | null;
};

export const RendererContentState_content: GraphQLTaggedNode = graphql`
	fragment RendererContentState_content on Content {
		id
		contentState(isDraft: false) {
			id
			name
			color
			restrictionLevel
		}
		contentStateLastUpdated(format: USER_FRIENDLY) {
			value
		}
	}
`;

const RendererContentStateContent = ({
	contentId,
	isDraft,
	hasSeparator,
	contentState,
	reload,
}: Pick<RendererContentStateProps, 'contentId' | 'isDraft' | 'hasSeparator'> & {
	contentState: RendererContentState_content$data['contentState'];
	reload?: () => Promise<any>;
}): React.JSX.Element => (
	<Fragment>
		{contentState && (
			<>
				<LazyRendererContentStateComponent
					contentId={contentId}
					contentState={contentState}
					isDraft={isDraft}
				/>
				{!isDraft && hasSeparator && <StyledBorder />}
			</>
		)}
		{reload && (
			<QuickReloadSubscriber name="content-state" types={[ReloadType.content]} reload={reload} />
		)}
	</Fragment>
);

const RendererContentStateFragmentReader = ({
	contentId,
	isDraft,
	hasSeparator,
	contentFragmentRef,
}: RendererContentStateProps & {
	contentFragmentRef: RendererContentState_content$key;
}): React.JSX.Element | null => {
	const content = useFragment(RendererContentState_content, contentFragmentRef);

	if (!content) {
		return null;
	}

	return (
		<RendererContentStateContent
			contentId={contentId}
			isDraft={isDraft}
			hasSeparator={hasSeparator}
			contentState={content.contentState}
		/>
	);
};

const RendererContentStateQuery = ({
	contentId,
	isDraft,
	hasSeparator,
}: RendererContentStateProps): React.JSX.Element | null => {
	const { data, refetch } = useQueryInterop<
		RendererContentStateQuery$data,
		RendererContentStateQuery$variables
	>(
		graphql`
			query RendererContentStateQuery(
				$contentId: ID!
				$isDraft: Boolean!
				$format: GraphQLDateFormat
			) {
				singleContent(id: $contentId) {
					id
					contentState(isDraft: $isDraft) {
						id
						name
						color
						restrictionLevel
					}
					contentStateLastUpdated(format: $format) {
						value
					}
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
			variables: {
				contentId,
				isDraft, // page preview uses draft version
				format: 'USER_FRIENDLY',
			},
		},
	);

	if (!data) {
		return null;
	}

	const contentState = data?.singleContent?.contentState;

	return (
		<RendererContentStateContent
			contentId={contentId}
			isDraft={isDraft}
			hasSeparator={hasSeparator}
			contentState={contentState ?? null}
			reload={refetch}
		/>
	);
};

export const RendererContentStateImpl = (
	props: RendererContentStateProps,
): React.JSX.Element | null => {
	const { contentFragmentRef, isDraft } = props;

	if (!isDraft && contentFragmentRef && fg('confluence_vp_meta_frag_reader')) {
		return (
			<RendererContentStateFragmentReader {...props} contentFragmentRef={contentFragmentRef} />
		);
	}

	return <RendererContentStateQuery {...props} />;
};

export const RendererContentState: React.FC<RendererContentStateProps> =
	withTransparentErrorBoundary({
		attribution: Attribution.PAGE_EXTENSIONS,
	})(RendererContentStateImpl);
